<script setup>
import * as Icons from 'lucide-vue-next/dist/esm/icons'
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/vue'
import { useDebounceFn } from '@vueuse/core'
import { Button } from '@codinglabsau/gooey'
import { toKebabCase } from 'lucide-vue-next/dist/esm/shared/src/utils.js'

const iconModules = import.meta.glob('/node_modules/lucide-vue-next/dist/esm/icons/*.js')

const value = defineModel()

const props = defineProps({
  error: {
    type: String,
    default: null,
  }
})

const query = ref('')
const edit = ref(true)

const updateQuery = useDebounceFn((newQuery) => {
  query.value = newQuery
}, 300)

onMounted(() => {
  if (value.value) {
    edit.value = false
  }
})

watch(value, (newValue) => {
  if (newValue !== null) {
    edit.value = false
  } else {
    edit.value = true
  }
})

const iconOptions = computed(() => {
  return Object.entries(Icons).map(([key]) => {
    return {
      name: key.replace(/([A-Z])/g, ' $1').replace(/([a-zA-Z])([0-9])/g, '$1 $2').trim(),
      value: toKebabCase(key).replace(/([^-])([0-9])/g, '$1-$2'),
    }
  })
})

const filteredIcons = computed(() => {
  if (query.value === '') {
    return []
  }

  return iconOptions.value
    .filter((icon) => icon.name.toLowerCase().includes(query.value.toLowerCase()))
    .map((icon) => {
      const filename = icon.value + '.js'
      const importer = iconModules[`/node_modules/lucide-vue-next/dist/esm/icons/${filename}`]

      return {
        ...icon,
        // Only resolve icon components for search results
        component: importer ? defineAsyncComponent(importer) : null,
      }
    })
})

const selectedIconComponent = computed(() => {
  if (!value.value) {
    return null
  }

  const filename = value.value + '.js'
  const importer = iconModules[`/node_modules/lucide-vue-next/dist/esm/icons/${filename}`]

  return importer ? defineAsyncComponent(importer) : null
})

const removeIcon = () => {
  value.value = null
}
</script>

<template>
  <Combobox v-model="value">
    <div class="relative mt-2">
      <ComboboxInput
        v-if="edit"
        class="text-sm"
        :class="error ? 'border-red-400' : 'border-gray-300'"
        @input="updateQuery($event.target.value)"
        :placeholder="__('Search Icons...')"
      />
      <div v-else class="flex items-center gap-2">
        <Component :is="selectedIconComponent" class="size-6" />
        <Button type="button" @click="removeIcon">{{ __('Remove Icon') }}</Button>
      </div>
      <ComboboxOptions class="absolute rounded-md shadow-md z-10 bg-white">
        <div class="w-full p-4 max-w-full text-xs text-center" v-if="filteredIcons.length === 0">
          {{ __('No icons found.') }}
        </div>
        <div v-else class="p-4 grid grid-cols-12 max-w-full max-h-72 overflow-y-scroll">
          <ComboboxOption
            as="div"
            role="button"
            v-for="icon in filteredIcons"
            :key="icon"
            :value="icon.value"
            class="flex size-8 items-center justify-center hover:bg-gray-50 hover:border hover:rounded"
            v-tooltip="{ content: icon.name }"
          >
            <Component
              :is="icon.component"
              class="w-5 h-5"
            />
          </ComboboxOption>
        </div>
      </ComboboxOptions>
    </div>
  </Combobox>
</template>
